import axios from './axiosConfig'
import { getEnv } from '@utils/env'
import { ReportPeriodPreset } from '@services/api/reports/finance.api'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'
import type { IClinic } from '@dataTypes/clinic.type'
import type { ReportPayloadData } from '@services/reports/reportPayloadData'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_CLINIC')

const headers = {
  'Content-Type': 'application/json',
}

export type PeriodCategory = 'latest-data' | 'historical' | 'snapshot'

export type SaveReportViewPayload = {
  clinic_report_view_id?: string
  page_id: string
  category?: string | null
  period_category?: PeriodCategory | null
  period_preset?: ReportPeriodPreset | null
  provider_filter?: string
  hist_range_start?: string
  hist_range_end?: string
  snapshot_as_of?: string
  snapshot_time?: string
  scope?: 'all' | 'custom'
  metrics?: Record<string, boolean> | null
  is_custom_report?: boolean
  custom_report_title?: string | null
  custom_report_categories?: Record<string, boolean>
  user_id?: string
  clinic_id?: string
  name?: string
}

export type ReportTableColumnSchema = { id: string; label: string }

export type ReportExtendedBlockSchema = {
  columns: ReportTableColumnSchema[]
  sample?: Record<string, unknown>[]
  headerTitle?: string
  headerSubtitle?: string
}

export type ReportMetricSchema = {
  id: string
  type?: string
  label: string
  pageTitle?: string
  customCategory?: string
  customLabel?: string
  columns?: ReportTableColumnSchema[]
  columns2?: ReportTableColumnSchema[]
  extendedBlocks?: ReportExtendedBlockSchema[]
  headerTitle?: string
  headerSubtitle1?: string
  headerTitle2?: string
  headerSubtitle2?: string
}

export type ReportsTemplateData = {
  metricsSchema: ReportMetricSchema[]
  html?: string
  css?: string
  [key: string]: unknown
}

export type SavedReportView = SaveReportViewPayload & {
  clinic_report_view_id: string
  name: string
}

type ReportApiResponse<T> = {
  statusCode: number
  message?: string
  data: T
}

type ReportReferenceValues = {
  user_id: string
  clinic_id: string
  db_name: string
}

type SaveReportSchedulePayload = SaveReportViewPayload & {
  every?: string
  every_details?: string
}

type DownloadReportPdfPayload = {
  selectedClinic: Partial<IClinic>
  reportTitle?: string | null
  reportFiltersAndMetrics: {
    filters: Record<string, string> | null
    metrics: Record<string, boolean> | null
  }
  generatedOnDisplay: string
  headerSwitch: boolean
  isCustomReport: boolean
  payloadData: ReportPayloadData
  user_id?: string
  clinic_id?: string
}

type PdfDownloadData = {
  pdfBase64: string
  filename?: string
}

const getReportReferenceValues = (): ReportReferenceValues => {
  const storedUser: unknown = JSON.parse(localStorage.getItem('user') || '{}')
  const userId =
    typeof storedUser === 'object' &&
    storedUser !== null &&
    'user_id' in storedUser &&
    typeof storedUser.user_id === 'string'
      ? storedUser.user_id
      : ''
  return {
    user_id: userId,
    clinic_id: localStorage.getItem('clinic_id') || '',
    db_name: localStorage.getItem('db_name') || '',
  }
}

// API Functions
export const getReportViews = async (
  pageId: string,
): Promise<ReportApiResponse<{ clinic_report_views: SavedReportView[] }>> => {
  try {
    const { clinic_id } = getReportReferenceValues()
    const response = await axios.get<
      ReportApiResponse<{ clinic_report_views: SavedReportView[] }>
    >(`${BUSINESS_LAYER_API}/api/v1/clinic/reports/views`, {
      headers,
      params: {
        page_id: pageId,
        clinic_id,
      },
    })

    return response.data
  } catch (e: unknown) {
    logger.error('Error in GetReportViews:', e)
    throw toApiError(e, 'Error fetching report views')
  }
}

export const saveOrUpdateReportView = async (
  data: SaveReportViewPayload,
): Promise<ReportApiResponse<{ clinic_report_view: SavedReportView }>> => {
  try {
    const { user_id, clinic_id } = getReportReferenceValues()
    data.user_id = user_id
    data.clinic_id = clinic_id
    const response = await axios.put<
      ReportApiResponse<{ clinic_report_view: SavedReportView }>
    >(`${BUSINESS_LAYER_API}/api/v1/clinic/reports/views`, data, {
      headers,
    })

    return response.data
  } catch (error: unknown) {
    logger.error('Error saving report view:', error)
    throw toApiError(error, 'Error saving report view')
  }
}

export const deleteReportView = async (
  viewId: string,
): Promise<ReportApiResponse<unknown>> => {
  try {
    const response = await axios.delete<ReportApiResponse<unknown>>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/reports/views/${viewId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error deleting report view:', error)
    throw toApiError(error, 'Error deleting report view')
  }
}

export const saveOrUpdateReportSchedule = async (
  data: SaveReportSchedulePayload,
): Promise<
  ReportApiResponse<{ clinic_report_schedule: Record<string, unknown> }>
> => {
  try {
    const { user_id, clinic_id } = getReportReferenceValues()
    data.user_id = user_id
    data.clinic_id = clinic_id
    const response = await axios.put<
      ReportApiResponse<{
        clinic_report_schedule: Record<string, unknown>
      }>
    >(`${BUSINESS_LAYER_API}/api/v1/clinic/reports/schedules`, data, {
      headers,
    })

    return response.data
  } catch (error: unknown) {
    logger.error('Error saving report schedule:', error)
    throw toApiError(error, 'Error saving report schedule')
  }
}

export const downloadReportPDF = async (
  data: DownloadReportPdfPayload,
): Promise<ReportApiResponse<PdfDownloadData>> => {
  try {
    const { user_id, clinic_id } = getReportReferenceValues()
    data.user_id = user_id
    data.clinic_id = clinic_id
    const response = await axios.post<ReportApiResponse<PdfDownloadData>>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/reports/generations/download-pdf`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error downloading report PDF:', error)
    throw toApiError(error, 'Error downloading report PDF')
  }
}

export const generateReportPDF = async (
  clinicReportScheduleId: string,
): Promise<ReportApiResponse<unknown>> => {
  try {
    const response = await axios.post<ReportApiResponse<unknown>>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/reports/generations/generate-scheduled-report`,
      {},
      {
        headers,
        params: {
          clinic_report_schedule_id: clinicReportScheduleId,
        },
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error downloading report PDF:', error)
    throw toApiError(error, 'Error downloading report PDF')
  }
}

export const processScheduledReports = async (): Promise<
  ReportApiResponse<unknown>
> => {
  try {
    const response = await axios.post<ReportApiResponse<unknown>>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/reports/generations/process-scheduled-reports`,
      {},
      {
        headers,
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error processing scheduled reports:', error)
    throw toApiError(error, 'Error processing scheduled reports')
  }
}

export const getReportsTemplate = async (): Promise<
  ReportApiResponse<ReportsTemplateData>
> => {
  try {
    const response = await axios.get<ReportApiResponse<ReportsTemplateData>>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/reports/generations/template`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error getting reports template:', error)
    throw toApiError(error, 'Error getting reports template')
  }
}
